/**
 * Money and date formatting. Financial timestamps are shown in Asia/Riyadh with the timezone made
 * explicit, and amounts are never treated as floating point in the UI beyond display
 * (spec sections 89 and 90).
 */

export const BUSINESS_TIME_ZONE = 'Asia/Riyadh';

/** Minor-unit count per currency, so a three-decimal currency is not shown with two.  */
const CURRENCY_DECIMALS: Record<string, number> = {
  SAR: 2,
  USD: 2,
  AED: 2,
  EUR: 2,
  GBP: 2,
  QAR: 2,
  EGP: 2,
  KWD: 3,
  BHD: 3,
  OMR: 3,
  JPY: 0,
};

export function currencyDecimals(currency: string | undefined): number {
  return CURRENCY_DECIMALS[(currency ?? 'SAR').toUpperCase()] ?? 2;
}

/** Formats an amount with its currency code, in the given locale. */
export function formatMoney(amount: number | null | undefined, currency = 'SAR', locale = 'en'): string {
  if (amount === null || amount === undefined) {
    return '—';
  }

  const decimals = currencyDecimals(currency);

  return new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-SA', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

/** Formats an amount without the currency symbol, for dense table columns. */
export function formatAmount(amount: number | null | undefined, currency = 'SAR', locale = 'en'): string {
  if (amount === null || amount === undefined) {
    return '—';
  }

  const decimals = currencyDecimals(currency);

  return new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-SA', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

export function formatNumber(value: number | null | undefined, locale = 'en'): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-SA').format(value);
}

export function formatPercent(value: number | null | undefined, locale = 'en'): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-SA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}%`;
}

/**
 * Date and time in the business timezone. The zone is shown because a financial timestamp must be
 * unambiguous (spec section 90).
 */
export function formatDateTime(value: string | null | undefined, locale = 'en'): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  const formatted = new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA' : 'en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: BUSINESS_TIME_ZONE,
  }).format(date);

  return `${formatted} (+03)`;
}

export function formatDate(value: string | null | undefined, locale = 'en'): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA' : 'en-GB', {
    dateStyle: 'medium',
    timeZone: BUSINESS_TIME_ZONE,
  }).format(date);
}

/** Short relative wording for "last seen" style fields. */
export function formatRelative(value: string | null | undefined, locale = 'en'): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const absolute = Math.abs(seconds);

  const formatter = new Intl.RelativeTimeFormat(locale === 'ar' ? 'ar' : 'en', { numeric: 'auto' });

  if (absolute < 60) return formatter.format(Math.round(seconds), 'second');
  if (absolute < 3_600) return formatter.format(Math.round(seconds / 60), 'minute');
  if (absolute < 86_400) return formatter.format(Math.round(seconds / 3_600), 'hour');
  return formatter.format(Math.round(seconds / 86_400), 'day');
}

/** Today's date in the business timezone as an ISO date, for default report ranges. */
export function businessToday(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

  return parts;
}

export function businessDaysAgo(days: number): string {
  const date = new Date(Date.now() - days * 86_400_000);

  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** Colour intent per payment status, used by badges (spec section 86). */
export type StatusIntent = 'success' | 'processing' | 'warning' | 'error' | 'default';

export function paymentStatusIntent(status: string | undefined): StatusIntent {
  switch (status) {
    case 'Succeeded':
    case 'Authorized':
      return 'success';
    case 'Pending':
    case 'Created':
    case 'Processing':
      return 'processing';
    case 'PartiallyRefunded':
    case 'Refunded':
    case 'UnderInvestigation':
      return 'warning';
    case 'Failed':
      return 'error';
    default:
      return 'default';
  }
}

export function subscriptionStatusIntent(status: string | undefined): StatusIntent {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Trial':
    case 'PendingActivation':
      return 'processing';
    case 'PastDue':
      return 'warning';
    case 'Suspended':
    case 'Cancelled':
    case 'Expired':
      return 'error';
    default:
      return 'default';
  }
}

export function refundStatusIntent(status: string | undefined): StatusIntent {
  switch (status) {
    case 'Succeeded':
      return 'success';
    case 'Processing':
    case 'Approved':
      return 'processing';
    case 'PendingApproval':
    case 'Requested':
      return 'warning';
    case 'Failed':
    case 'Rejected':
      return 'error';
    default:
      return 'default';
  }
}

export function healthIntent(status: string | undefined): StatusIntent {
  switch (status) {
    case 'Healthy':
      return 'success';
    case 'Degraded':
      return 'warning';
    case 'Unhealthy':
      return 'error';
    default:
      return 'default';
  }
}

export function severityIntent(severity: string | undefined): StatusIntent {
  switch (severity) {
    case 'Critical':
    case 'Error':
      return 'error';
    case 'Warning':
      return 'warning';
    case 'Info':
      return 'processing';
    default:
      return 'default';
  }
}
